import { Link } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { useContact } from "@/hooks/useContact";
import { usePages } from "@/hooks/usePages";
import { T } from "@/context/ContentContext";

const COLS = [
  { title: "Trips", links: [["Georgia Specials", "/georgia"], ["Georgia Visa Guide", "/georgia/visa-guide"], ["Group Trips", "/trips?category=group"], ["Honeymoons", "/trips?category=honeymoon"], ["Weekend Getaways", "/trips?category=weekend"]] },
  { title: "Explore", links: [["India", "/trips?region=india"], ["International", "/trips?region=international"], ["Contact", "/contact"], ["Corporate Offsites", "/contact?type=corporate"]] },
];

function useFooterCols() {
  const { pages, isHidden } = usePages();
  const visible = (links) => links.filter(([, to]) => !isHidden(to.split("?")[0]));
  const dest = pages.filter((p) => p.kind === "destination" && !p.hidden).map((p) => [p.name, p.path]);
  return COLS.map((c, i) => ({ ...c, links: visible(i === 1 ? [...dest, ...c.links] : c.links) }));
}

export function Footer() {
  const CONTACT = useContact();
  const cols = useFooterCols();
  return (
    <footer className="relative bg-ink text-cream overflow-hidden grain" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-10 relative z-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5 space-y-6">
            <Logo light />
            <T k="footer.about" as="p" className="text-cream/75 max-w-sm leading-relaxed">
              A Delhi travel agency for group trips, honeymoons and custom journeys across India and abroad, with Georgia as our home turf.
            </T>
            <div className="space-y-3 text-sm">
              <a href={`tel:+91${CONTACT.phone}`} data-testid="footer-phone" className="flex items-center gap-3 hover:text-terra-soft transition-colors"><Phone className="h-4 w-4 text-gold" />{CONTACT.phoneDisplay}</a>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="flex items-center gap-3 hover:text-terra-soft transition-colors"><Mail className="h-4 w-4 text-gold" />{CONTACT.email}</a>
              <p className="flex items-start gap-3 text-cream/80" data-testid="footer-address"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-gold" />{CONTACT.address}</p>
            </div>
          </div>
          {cols.map((c) => (
            <div key={c.title} className="lg:col-span-2">
              <p className="eyebrow text-gold mb-5">{c.title}</p>
              <ul className="space-y-3">
                {c.links.map(([label, to]) => (
                  <li key={label}><Link to={to} className="text-cream/80 hover:text-cream transition-colors">{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
          <div className="lg:col-span-3">
            <T k="footer.cta-eyebrow" as="p" className="eyebrow text-gold mb-5">Start here</T>
            <Link to="/georgia" data-testid="footer-georgia-cta" className="font-serif italic text-4xl leading-tight hover:text-terra-soft transition-colors">
              <T k="footer.cta">Georgia is calling →</T>
            </Link>
          </div>
        </div>
        <p aria-hidden className="mt-20 font-display font-black tracking-[-0.06em] leading-[0.8] text-[22vw] lg:text-[17rem] text-cream/[0.06] select-none whitespace-nowrap">
          trippytastic
        </p>
        <div className="mt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-cream/60 font-mono">
          <span>© {new Date().getFullYear()} Trippy Tastic. All rights reserved.</span>
          <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-cream">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
